
import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { SecurityProvider } from "@/components/security/SecurityProvider";
import SecureAuthGate from "@/components/auth/SecureAuthGate";

// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const EventEditor = lazy(() => import("./pages/EventEditor"));
const EventPage = lazy(() => import("./pages/EventPage"));
const EventAttendees = lazy(() => import("./pages/EventAttendees"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const UserProfilePage = lazy(() => import("./pages/UserProfilePage"));
const Login = lazy(() => import("./pages/Login"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ClerkDemo = lazy(() => import("./pages/ClerkDemo"));
const Register = lazy(() => import("./pages/Register"));
const OrganizationsPage = lazy(() => import("./pages/Organizations"));
const OrgSettingsPage = lazy(() => import("./pages/org/OrgSettings"));
const OrgMembersPage = lazy(() => import("./pages/org/OrgMembers"));

const AcceptInvitePage = lazy(() => import("./pages/org/AcceptInvite"));
const OrgPublicPage = lazy(() => import("./pages/public/OrgPublic"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on authentication errors
        if (error?.message?.includes('Invalid API key') || 
            error?.message?.includes('Unauthorized')) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <SecurityProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <BrowserRouter>
              <Suspense fallback={
                <div className="flex items-center justify-center h-screen" role="status" aria-busy="true">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" aria-hidden="true"></div>
                  <span className="sr-only">Loading application...</span>
                </div>
              }>
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={
                    <SecureAuthGate requireAuth={false}>
                      <Index />
                    </SecureAuthGate>
                  } />
                  <Route path="/login" element={
                    <SecureAuthGate requireAuth={false}>
                      <Login />
                    </SecureAuthGate>
                  } />
                  <Route path="/register" element={
                    <SecureAuthGate requireAuth={false}>
                      <Register />
                    </SecureAuthGate>
                  } />
                  <Route path="/explore" element={
                    <SecureAuthGate requireAuth={false}>
                      <Dashboard />
                    </SecureAuthGate>
                  } />
                  <Route path="/:slug" element={
                    <SecureAuthGate requireAuth={false}>
                      <EventPage />
                    </SecureAuthGate>
                  } />
                  <Route path="/user/:userId" element={
                    <SecureAuthGate requireAuth={false}>
                      <UserProfilePage />
                    </SecureAuthGate>
                  } />
                  <Route path="/profile/:userId" element={
                    <SecureAuthGate requireAuth={false}>
                      <UserProfilePage />
                    </SecureAuthGate>
                  } />

                  {/* Protected routes */}
                  <Route path="/dashboard" element={
                    <SecureAuthGate requireAuth={true}>
                      <Dashboard />
                    </SecureAuthGate>
                  } />
                  <Route path="/organizations" element={
                    <SecureAuthGate requireAuth={true}>
                      <OrganizationsPage />
                    </SecureAuthGate>
                  } />
                  <Route path="/organizations/:id/settings" element={
                    <SecureAuthGate requireAuth={true}>
                      <OrgSettingsPage />
                    </SecureAuthGate>
                  } />
                  <Route path="/o/:slug/settings" element={
                    <SecureAuthGate requireAuth={true}>
                      <OrgSettingsPage />
                    </SecureAuthGate>
                  } />
                  <Route path="/organizations/:id/members" element={
                    <SecureAuthGate requireAuth={true}>
                      <OrgMembersPage />
                    </SecureAuthGate>
                  } />
                  <Route path="/o/:slug/members" element={
                    <SecureAuthGate requireAuth={true}>
                      <OrgMembersPage />
                    </SecureAuthGate>
                  } />
                  
                   <Route path="/o/:slug/settings" element={
                     <SecureAuthGate requireAuth={true}>
                       <OrgSettingsPage />
                     </SecureAuthGate>
                   } />
                   <Route path="/organizations/invite/:token" element={
                    <SecureAuthGate requireAuth={true}>
                      <AcceptInvitePage />
                    </SecureAuthGate>
                  } />
                  {/* Public organization page and invite acceptance by slug */}
                  <Route path="/o/:slug" element={<OrgPublicPage />} />
                  <Route path="/o/:slug/invite/:token" element={
                    <SecureAuthGate requireAuth={true}>
                      <AcceptInvitePage />
                    </SecureAuthGate>
                  } />
                  <Route path="/dashboard/:eventId" element={
                    <SecureAuthGate requireAuth={true}>
                      <EventAttendees />
                    </SecureAuthGate>
                  } />
                  <Route path="/dashboard/:eventId/edit" element={
                    <SecureAuthGate requireAuth={true}>
                      <EventEditor />
                    </SecureAuthGate>
                  } />
                  <Route path="/dashboard/new" element={
                    <SecureAuthGate requireAuth={true}>
                      <EventEditor />
                    </SecureAuthGate>
                  } />
                  <Route path="/profile" element={
                    <SecureAuthGate requireAuth={true}>
                      <ProfilePage />
                    </SecureAuthGate>
                  } />
                  <Route path="/clerk-demo" element={
                    <SecureAuthGate requireAuth={true}>
                      <ClerkDemo />
                    </SecureAuthGate>
                  } />

                  {/* 404 route */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </SecurityProvider>
    </HelmetProvider>
  );
}

export default App;
